import React, { useState, useEffect } from "react"
import ReactDOM from "react-dom"
import { get, put } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"

const UserRoleAssignment = () => {
  let [adminRoles, setAdminRoles] = useState([])
  const [loading, setLoading] = useState(false)
  const [roleToChange, setRoleToChange] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [hoveredRow, setHoveredRow] = useState(null)

  // Search Filter
  const filteredAdminRoles = adminRoles?.filter((adminRole) => {
    return Object.entries(adminRole).some(([key, value]) => {
      // For other fields, apply standard filtering
      return value
        ?.toString()
        .trim()
        .toLowerCase()
        .includes(searchText.toLowerCase().trim())
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentAdminRoles = filteredAdminRoles.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredAdminRoles.length / rowsPerPage)

  useEffect(() => {
    loadAppConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAppConfigs = async () => {
    try {
      const data = await get("/admin/all/ssoCustomers")

      setAdminRoles(data)
      setCurrentPage(1) // reset to first page
    } catch (error) {
      console.error("Failed to fetch app configs", error)
    }
  }

  const loadAppConfigs = async () => {
    setLoading(true)
    await fetchAppConfigs()
    setLoading(false)
  }

  const handleRoleChange = async (role) => {
    const { emailAddress, prevRole, newRole } = role
    try {
      setAdminRoles((prev) =>
        prev.map((config) =>
          config.emailAddress === emailAddress
            ? { ...config, userRole: newRole }
            : config,
        ),
      )

      await put("/admin/updateRole", { emailAddress, newRole, prevRole })

      toast.success("Role updated successfully")
    } catch (error) {
      console.error("Failed to update role", error)
      toast.error(error)
      setAdminRoles((prev) =>
        prev.map((config) =>
          config.emailAddress === emailAddress
            ? { ...config, userRole: prevRole }
            : config,
        ),
      )
    }
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", width: "100%", overflow: "hidden", boxSizing: "border-box" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <div className="setting-note">
              Allows Super Admin to configure and assign roles to users
            </div>
            <div style={{ flexShrink: 0 }}>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={`Search App User...`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                  style={{ width: "200px", paddingRight: "22px" }}
                />
                <button
                  type="button"
                  onClick={() => { setSearchText(""); setCurrentPage(1) }}
                  className="search-clear-btn"
                  style={{ visibility: searchText ? "visible" : "hidden" }}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">App User</th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentAdminRoles.length > 0 ? (
                  currentAdminRoles.map((config) => (
                    <tr
                      key={config.appId || config.emailAddress}
                      className={`fax-item${hoveredRow === config.emailAddress ? " row-highlighted" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => setHoveredRow(config.emailAddress)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.emailAddress}
                      </td>
                      <td
                        className="faxes-td"
                        style={{ width: "100px", textAlign: "center" }}
                      >
                        <select
                          className="select-dropdown"
                          style={{ width: "200px" }}
                          value={config.userRole}
                          onChange={(e) => {
                            setRoleToChange({
                              emailAddress: config.emailAddress,
                              prevRole: config.userRole,
                              newRole: e.target.value,
                            })
                          }}
                        >
                          <option value="ADMIN">ADMIN</option>
                          <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                          <option value="USER">USER</option>
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredAdminRoles.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredAdminRoles.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredAdminRoles.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {roleToChange && (
        <ConfirmationPopup
          onConfirm={() => {
            handleRoleChange(roleToChange)
            setRoleToChange(null)
          }}
          onCancel={() => setRoleToChange(null)}
          roleToChange={roleToChange}
        />
      )}
    </div>
  )
}

const ConfirmationPopup = ({ onConfirm, onCancel, roleToChange }) => ReactDOM.createPortal(
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px" }}>
      <p
        style={{ marginTop: "0px" }}
      >{`Are you sure you want to change the role of ${roleToChange.emailAddress} to ${roleToChange.newRole}?`}</p>
      <div className="popup-buttons">
        <button className="cancel" onClick={onCancel}>
          No
        </button>
        <button className="save" onClick={onConfirm}>
          Yes
        </button>
      </div>
    </div>
  </div>,
  document.body
)

export default UserRoleAssignment
